import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// 🎯 LANDING PAGE - SCOPRI MY SUBBUTEO MANAGER
const LandingPage = ({ onNavigateToApp, onNavigateToRegister }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState({});
  const [activeFeature, setActiveFeature] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle scroll for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animate sections on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-section').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 5);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    { icon: '🏆', title: 'Organizzare i tuoi tornei', desc: 'Gestisci ogni competizione con facilità' },
    { icon: '📅', title: 'Pianificare il calendario', desc: 'Mai più date dimenticate' },
    { icon: '⚽', title: 'Registrare partite', desc: 'Ogni risultato conta, salvalo' },
    { icon: '📊', title: 'Analizzare statistiche', desc: 'Scopri i tuoi punti di forza' },
    { icon: '📚', title: 'Creare uno storico', desc: 'La tua carriera Subbuteo documentata' },
  ];

  const challenges = [
    { day: 1, task: 'Registrati e accedi alla tua area personale', icon: '👤' },
    { day: 2, task: 'Inserisci un torneo a cui hai partecipato', icon: '🏆' },
    { day: 3, task: 'Aggiungi i risultati di almeno 3 partite', icon: '⚽' },
    { day: 4, task: 'Esplora la tua pagina delle statistiche', icon: '🔍' },
    { day: 5, task: 'Pianifica un prossimo torneo nel calendario', icon: '📅' },
    { day: 6, task: 'Aggiorna i risultati di una partita recente', icon: '✏️' },
    { day: 7, task: 'Analizza i tuoi progressi e le statistiche', icon: '📈' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* 🔝 NAVBAR FISSA */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-lg' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <span className="text-xl sm:text-2xl">⚽</span>
              <span className={`text-base sm:text-xl font-bold ${isScrolled ? 'text-gray-800' : 'text-gray-800'}`}>
                <span className="text-green-600">My</span>Subbuteo<span className="text-green-600 hidden xs:inline">Manager</span>
              </span>
            </a>

            {/* Navigation Links - Desktop */}
            <div className="hidden lg:flex items-center gap-6">
              <a href="#funzionalita" className={`font-medium hover:text-green-600 transition-colors ${isScrolled ? 'text-gray-600' : 'text-gray-700'}`}>
                Funzionalità
              </a>
              <a href="#come-funziona" className={`font-medium hover:text-green-600 transition-colors ${isScrolled ? 'text-gray-600' : 'text-gray-700'}`}>
                Come Funziona
              </a>
              <a href="#contatti" className={`font-medium hover:text-green-600 transition-colors ${isScrolled ? 'text-gray-600' : 'text-gray-700'}`}>
                Contatti
              </a>
              <button 
                onClick={() => navigate('/guida')}
                className={`font-medium hover:text-purple-600 transition-colors flex items-center gap-1 ${isScrolled ? 'text-purple-600' : 'text-purple-700'}`}
              >
                📖 Guida
              </button>
              <button 
                onClick={() => navigate('/login-club')}
                className={`font-medium hover:text-blue-600 transition-colors flex items-center gap-1 ${isScrolled ? 'text-blue-600' : 'text-blue-700'}`}
              >
                🏢 Area Club
              </button>
            </div>

            {/* CTA Buttons - Desktop */}
            <div className="hidden md:flex items-center gap-2 sm:gap-3">
              <button
                onClick={onNavigateToApp}
                className={`px-3 sm:px-4 py-2 font-semibold rounded-full transition-all duration-300 text-sm sm:text-base ${
                  isScrolled
                    ? 'text-blue-600 hover:bg-blue-50 border border-blue-200'
                    : 'text-blue-700 hover:bg-white/50 border border-blue-300'
                }`}
                data-testid="landing-login-btn"
              >
                🎮 Giocatore
              </button>
              <button
                onClick={() => navigate('/login-club')}
                className="px-3 sm:px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-full shadow-lg hover:shadow-green-500/30 transform hover:scale-105 transition-all duration-300 text-sm sm:text-base"
                data-testid="landing-club-btn"
              >
                🏆 Area Club
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4 space-y-3 bg-white">
              <a href="#funzionalita" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-gray-700 hover:text-green-600">Funzionalità</a>
              <a href="#come-funziona" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-gray-700 hover:text-green-600">Come Funziona</a>
              <a href="#contatti" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-gray-700 hover:text-green-600">Contatti</a>
              <button onClick={() => { navigate('/guida'); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-purple-600">📖 Guida</button>
              <div className="flex flex-col gap-2 pt-3 border-t border-gray-200">
                <button
                  onClick={() => { onNavigateToApp(); setMobileMenuOpen(false); }}
                  className="w-full py-3 text-center font-semibold rounded-xl border-2 border-blue-200 text-blue-600 hover:bg-blue-50"
                >
                  🎮 Area Giocatore
                </button>
                <button
                  onClick={() => { navigate('/login-club'); setMobileMenuOpen(false); }}
                  className="w-full py-3 text-center font-semibold rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                >
                  🏆 Area Club
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* 🎯 HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 text-9xl">⚽</div>
          <div className="absolute top-40 right-20 text-8xl">🏆</div>
          <div className="absolute bottom-40 left-1/4 text-7xl">📊</div>
          <div className="absolute bottom-20 right-1/4 text-8xl">🥅</div>
        </div>

        {/* Animated Gradient Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
          {/* Logo */}
          <div className="mb-6">
            <span className="text-6xl md:text-7xl">⚽</span>
          </div>

          {/* Main Title */}
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-4 leading-tight">
            <span className="text-green-600">My</span>Subbuteo
            <span className="text-green-600">Manager</span>
          </h1>

          {/* Subtitle - Spiegazione chiara */}
          <p className="text-lg md:text-xl text-gray-600 mb-4 max-w-3xl mx-auto">
            La piattaforma completa per il mondo del Subbuteo
          </p>
          <p className="text-base md:text-lg text-gray-500 mb-10 max-w-2xl mx-auto">
            Gestisci il tuo club o partecipa ai tornei della tua zona. 
            Tutto in un unico posto, gratis.
          </p>

          {/* DUE CARD PRINCIPALI */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-10">
            
            {/* CARD GIOCATORE */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-100 hover:border-blue-400 transition-all duration-300 hover:shadow-blue-200/50 hover:shadow-2xl overflow-hidden group">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
                <div className="text-4xl mb-2">🎮</div>
                <h3 className="text-xl font-bold text-white">Sono un GIOCATORE</h3>
              </div>
              <div className="p-6">
                <ul className="text-left text-gray-600 space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">✓</span> Iscriviti ai tornei nella tua zona
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">✓</span> Monitora le tue statistiche personali
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">✓</span> Segui la tua carriera nel Subbuteo
                  </li>
                </ul>
                <div className="space-y-3">
                  <button
                    onClick={onNavigateToRegister || onNavigateToApp}
                    className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors"
                    data-testid="hero-player-register-btn"
                  >
                    Registrati come Giocatore
                  </button>
                  <button
                    onClick={onNavigateToApp}
                    className="w-full py-3 border-2 border-blue-200 text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-colors"
                    data-testid="hero-player-login-btn"
                  >
                    Accedi al tuo account
                  </button>
                </div>
              </div>
            </div>

            {/* CARD CLUB */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-green-100 hover:border-green-400 transition-all duration-300 hover:shadow-green-200/50 hover:shadow-2xl overflow-hidden group">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4">
                <div className="text-4xl mb-2">🏆</div>
                <h3 className="text-xl font-bold text-white">Gestisco un CLUB</h3>
              </div>
              <div className="p-6">
                <ul className="text-left text-gray-600 space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Organizza tornei e campionati
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Gestisci soci, quote e finanze
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Calendario eventi e iscrizioni online
                  </li>
                </ul>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/registra-club')}
                    className="w-full py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-colors"
                    data-testid="hero-club-register-btn"
                  >
                    Registra il tuo Club
                  </button>
                  <button
                    onClick={() => navigate('/login-club')}
                    className="w-full py-3 border-2 border-green-200 text-green-600 font-semibold rounded-xl hover:bg-green-50 transition-colors"
                    data-testid="hero-club-login-btn"
                  >
                    Accedi all&apos;Area Club
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Badge */}
          <p className="text-gray-500 text-sm">
            ✅ 100% Gratuito • ✅ Semplice da usare • ✅ Pensato da subbuteisti per subbuteisti
          </p>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-green-400 rounded-full flex justify-center pt-2">
            <div className="w-2 h-3 bg-green-400 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* 😫 IL PROBLEMA */}
      <section id="problema" className="py-24 bg-gradient-to-b from-gray-50 to-white animate-section">
        <div className={`max-w-6xl mx-auto px-6 transition-all duration-1000 ${isVisible.problema ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Se giochi a Subbuteo, <br />
              <span className="text-red-500">conosci già questo caos.</span>
            </h2>
          </div>

          {/* Problem Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Chat Chaos */}
            <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border-l-4 border-red-400">
              <div className="text-5xl mb-4">📱</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Chat infinite</h3>
              <p className="text-gray-600">Risultati persi tra messaggi WhatsApp, date confuse, "ma quando era quel torneo?"</p>
            </div>

            {/* Paper Notes */}
            <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border-l-4 border-orange-400">
              <div className="text-5xl mb-4">📝</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Appunti sparsi</h3>
              <p className="text-gray-600">Foglietti con risultati, quaderni dimenticati, dati che non trovi più.</p>
            </div>

            {/* Memory */}
            <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border-l-4 border-yellow-400">
              <div className="text-5xl mb-4">🧠</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Affidarsi alla memoria</h3>
              <p className="text-gray-600">"Contro chi ho più vittorie?" "Quante partite ho giocato quest'anno?"</p>
            </div>

            {/* Lost Data */}
            <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border-l-4 border-purple-400">
              <div className="text-5xl mb-4">❓</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Domande senza risposta</h3>
              <p className="text-gray-600">"Dove ho segnato di più?" "Come sto andando questa stagione?"</p>
            </div>
          </div>

          {/* Big Question */}
          <div className="text-center bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-200">
            <p className="text-2xl font-bold text-gray-800">
              😤 Basta disordine. <br />
              <span className="text-green-600">È ora di cambiare.</span>
            </p>
          </div>
        </div>
      </section>

      {/* 💡 LA SOLUZIONE */}
      <section id="soluzione" className="py-24 bg-gradient-to-b from-green-600 to-emerald-700 text-white animate-section">
        <div className={`max-w-6xl mx-auto px-6 transition-all duration-1000 ${isVisible.soluzione ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Mettiamo fine al disordine.
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              <span className="font-bold text-white">MySubbuteoManager.com</span> nasce per risolvere proprio questo problema.
            </p>
          </div>

          {/* Solution Highlight */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-10 text-center border border-white/20">
            <div className="text-6xl mb-6">🎯</div>
            <h3 className="text-3xl font-bold mb-4">
              Lo strumento definitivo
            </h3>
            <p className="text-xl text-green-100 mb-8">
              Pensato da subbuteisti per subbuteisti, <br />
              per avere <span className="text-white font-bold">tutto in un unico posto.</span>
            </p>
            <div className="inline-block bg-white text-green-600 px-8 py-3 rounded-full font-bold text-lg">
              ✨ Semplice e intuitivo
            </div>
          </div>
        </div>
      </section>

      {/* 🛠️ FUNZIONALITÀ */}
      <section id="funzionalita" className="py-24 bg-white animate-section">
        <div className={`max-w-6xl mx-auto px-6 transition-all duration-1000 ${isVisible.funzionalita ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Molto più di un archivio: <br />
              <span className="text-green-600">il tuo centro di controllo Subbuteo.</span>
            </h2>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-5 gap-6 mb-12">
            {features.map((feature, index) => (
              <div
                key={index}
                onClick={() => setActiveFeature(index)}
                className={`cursor-pointer p-6 rounded-2xl text-center transition-all duration-500 transform hover:scale-105 ${
                  activeFeature === index
                    ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-2xl scale-105'
                    : 'bg-gray-50 text-gray-700 hover:bg-green-50'
                }`}
              >
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="font-bold text-sm md:text-base">{feature.title}</h3>
              </div>
            ))}
          </div>

          {/* Active Feature Description */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 text-center border-2 border-green-200">
            <p className="text-2xl text-gray-700">
              <span className="text-4xl mr-3">{features[activeFeature].icon}</span>
              <span className="font-bold text-green-600">{features[activeFeature].title}</span>
              <br />
              <span className="text-lg text-gray-600">{features[activeFeature].desc}</span>
            </p>
          </div>
        </div>
      </section>

      {/* 👥 PER CHI */}
      <section id="per-chi" className="py-24 bg-gray-50 animate-section">
        <div className={`max-w-6xl mx-auto px-6 transition-all duration-1000 ${isVisible['per-chi'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Costruito per adattarsi <br />
              <span className="text-green-600">al tuo modo di giocare.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Giocatori */}
            <div className="bg-white rounded-3xl shadow-xl p-10 text-center transform hover:scale-105 transition-all duration-300 border-t-4 border-blue-500">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">👤</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Giocatori</h3>
              <ul className="text-gray-600 space-y-2 text-left">
                <li>✅ Traccia le tue partite</li>
                <li>✅ Analizza le tue statistiche</li>
                <li>✅ Scopri i tuoi punti di forza</li>
                <li>✅ Segui la tua crescita</li>
              </ul>
            </div>

            {/* Organizzatori */}
            <div className="bg-white rounded-3xl shadow-xl p-10 text-center transform hover:scale-105 transition-all duration-300 border-t-4 border-green-500">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🏆</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Organizzatori</h3>
              <ul className="text-gray-600 space-y-2 text-left">
                <li>✅ Gestisci i tuoi tornei</li>
                <li>✅ Salva lo storico</li>
                <li>✅ Pianifica la stagione</li>
                <li>✅ Tieni tutto organizzato</li>
              </ul>
            </div>

            {/* Club */}
            <div className="bg-white rounded-3xl shadow-xl p-10 text-center transform hover:scale-105 transition-all duration-300 border-t-4 border-purple-500 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-purple-500 text-white text-xs px-3 py-1 rounded-full font-bold">
                🚀 IN ARRIVO
              </div>
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🏟️</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Club</h3>
              <ul className="text-gray-600 space-y-2 text-left">
                <li>✅ Statistiche soci</li>
                <li>✅ Tornei interni</li>
                <li>✅ Classifiche club</li>
                <li>✅ Comunicazioni</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 🚀 COME FUNZIONA */}
      <section id="come-funziona" className="py-24 bg-white animate-section">
        <div className={`max-w-6xl mx-auto px-6 transition-all duration-1000 ${isVisible['come-funziona'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Inizia subito: <br />
              <span className="text-green-600">il tuo account è pronto in un minuto.</span>
            </h2>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-3xl p-8 text-center shadow-xl">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-white text-green-600 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                  1
                </div>
                <div className="text-5xl mb-4">🌐</div>
                <h3 className="text-xl font-bold mb-2">Visita il sito</h3>
                <p className="text-green-100">mysubbuteomanager.com</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-3xl p-8 text-center shadow-xl">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-white text-blue-600 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                  2
                </div>
                <div className="text-5xl mb-4">📝</div>
                <h3 className="text-xl font-bold mb-2">Registrati</h3>
                <p className="text-blue-100">Nome, Email, Password</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 text-white rounded-3xl p-8 text-center shadow-xl">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-white text-purple-600 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                  3
                </div>
                <div className="text-5xl mb-4">🚀</div>
                <h3 className="text-xl font-bold mb-2">Inizia!</h3>
                <p className="text-purple-100">Il tuo spazio personale è pronto</p>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-green-50 rounded-2xl p-8 text-center border-2 border-green-200">
            <p className="text-lg text-gray-700">
              💡 <span className="font-bold">Ogni utente ha il proprio spazio personale e sicuro</span> <br />
              dove creare tornei, inserire partite e visualizzare statistiche.
            </p>
          </div>
        </div>
      </section>

      {/* 📊 LE STATISTICHE */}
      <section id="statistiche" className="py-24 bg-gradient-to-b from-gray-900 to-gray-800 text-white animate-section">
        <div className={`max-w-6xl mx-auto px-6 transition-all duration-1000 ${isVisible.statistiche ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Le statistiche: <br />
              <span className="text-green-400">il cuore di MySubbuteoManager.</span>
            </h2>
            <p className="text-xl text-gray-400">Non solo numeri, ma la verità sul tuo gioco.</p>
          </div>

          {/* Stats Preview */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">87</div>
              <div className="text-gray-400">Partite giocate</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">54</div>
              <div className="text-gray-400">Vittorie</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-yellow-400 mb-2">62%</div>
              <div className="text-gray-400">% Successo</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">📈</div>
              <div className="text-gray-400">Andamento</div>
            </div>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
              <div className="text-3xl mb-4">💪</div>
              <h3 className="text-xl font-bold text-green-400 mb-3">Capire i tuoi punti di forza</h3>
              <p className="text-gray-400">Contro chi vinci di più? In quali situazioni rendi meglio?</p>
            </div>
            <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
              <div className="text-3xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-blue-400 mb-3">Individuare cosa migliorare</h3>
              <p className="text-gray-400">Qual è il tuo tallone d'Achille? Dove puoi crescere?</p>
            </div>
            <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
              <div className="text-3xl mb-4">📈</div>
              <h3 className="text-xl font-bold text-purple-400 mb-3">Seguire la tua crescita</h3>
              <p className="text-gray-400">Misura i progressi, stagione dopo stagione.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 🎮 SFIDA 7 GIORNI */}
      <section id="sfida" className="py-24 bg-gradient-to-b from-orange-500 to-red-500 text-white animate-section">
        <div className={`max-w-6xl mx-auto px-6 transition-all duration-1000 ${isVisible.sfida ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <div className="inline-block bg-white/20 backdrop-blur rounded-full px-6 py-2 mb-6">
              <span className="text-lg font-bold">🎯 LA TUA MINI-SFIDA</span>
            </div>
            <h2 className="text-5xl md:text-7xl font-extrabold mb-6">
              7 GIORNI
            </h2>
            <p className="text-xl text-orange-100 max-w-2xl mx-auto">
              Organizza completamente il tuo Subbuteo in una sola settimana. <br />
              <span className="font-bold text-white">Sei pronto?</span>
            </p>
          </div>

          {/* Challenge Days */}
          <div className="grid md:grid-cols-7 gap-4 mb-12">
            {challenges.map((challenge, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur rounded-2xl p-4 text-center hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
              >
                <div className="w-12 h-12 bg-white text-orange-500 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-xl">
                  {challenge.day}
                </div>
                <div className="text-2xl mb-2">{challenge.icon}</div>
                <p className="text-xs text-orange-100">{challenge.task}</p>
              </div>
            ))}
          </div>

          {/* Success Message */}
          <div className="bg-white rounded-3xl p-10 text-center shadow-2xl">
            <div className="text-5xl mb-4">🏆</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Missione compiuta!</h3>
            <p className="text-gray-600 mb-6">
              Se completi la sfida, avrai ufficialmente messo ordine nel tuo Subbuteo. <br />
              <span className="font-bold text-green-600">Benvenuto nel tuo nuovo centro di controllo.</span>
            </p>
            <button
              onClick={onNavigateToRegister || onNavigateToApp}
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xl font-bold rounded-full shadow-xl hover:shadow-green-500/50 transform hover:scale-105 transition-all duration-300"
              data-testid="challenge-start-btn"
            >
              🚀 Inizia la Sfida
            </button>
          </div>
        </div>
      </section>

      {/* 💬 CITAZIONE FINALE */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-6xl mb-8">💡</div>
          <blockquote className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 leading-relaxed">
            "Un ultimo consiglio, <br />
            da giocatore a giocatore. <br />
            <span className="text-green-600">Non affidarti alla memoria.</span> <br />
            I dati raccontano sempre <br />
            la verità del tuo gioco."
          </blockquote>
          <p className="text-xl text-gray-600 mb-12">
            Inizia oggi a scrivere la tua vera storia Subbuteo.
          </p>
          <button
            onClick={onNavigateToRegister || onNavigateToApp}
            className="px-10 py-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-2xl font-bold rounded-full shadow-2xl hover:shadow-green-500/50 transform hover:scale-105 transition-all duration-300"
            data-testid="final-register-btn"
          >
            🚀 Registrati Gratis
          </button>
        </div>
      </section>

      {/* 📞 CONTATTI E SOCIAL */}
      <section id="contatti" className="py-16 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 items-center">
            {/* Logo & Tagline */}
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2">
                <span className="text-green-400">My</span>Subbuteo<span className="text-green-400">Manager</span>
              </h3>
              <p className="text-gray-400">Organizza. Analizza. Migliora.</p>
            </div>

            {/* Contact */}
            <div className="text-center">
              <h4 className="text-lg font-bold mb-4 text-green-400">📧 Contattaci</h4>
              <a 
                href="mailto:mysubbuteomanager@gmail.com" 
                className="text-gray-300 hover:text-white transition-colors"
              >
                mysubbuteomanager@gmail.com
              </a>
            </div>

            {/* Social */}
            <div className="text-center md:text-right">
              <h4 className="text-lg font-bold mb-4 text-green-400">🌐 Seguici</h4>
              <div className="flex justify-center md:justify-end gap-6">
                <a
                  href="https://www.facebook.com/share/1DYfcXmRQ6/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors transform hover:scale-110"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/mysubbuteomanager?igsh=dXJ6MnJ4bGNldzQ2&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity transform hover:scale-110"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500 text-sm">
            <p>© 2025 MySubbuteoManager. Tutti i diritti riservati.</p>
            <p className="mt-2">Pensato da subbuteisti per subbuteisti ⚽</p>
          </div>
        </div>
      </section>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
